import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

const OrganizationSMSModal = ({ organization, onClose, onSuccess }) => {
  const [recipientType, setRecipientType] = useState('team');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Data for recipient selection
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedAgeGroupId, setSelectedAgeGroupId] = useState('');

  useEffect(() => {
    if (organization) {
      fetchTeams();
      fetchUsers();
      fetchAgeGroups();
    }
  }, [organization]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`/api/organizations/${organization.id}/teams`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTeams(response.data || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`/api/organizations/${organization.id}/members`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      // Transform OrganizationUser objects to User objects
      const userList = (response.data || []).map(orgUser => ({
        id: orgUser.User?.id || orgUser.user_id,
        name: orgUser.User?.name || orgUser.User?.email || 'Unknown User',
        email: orgUser.User?.email || '',
        phone: orgUser.User?.phone || '',
        role: orgUser.User?.role || 'viewer'
      })).filter(user => user.id && user.phone); // Only show users with phone numbers
      setUsers(userList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchAgeGroups = async () => {
    try {
      const response = await axios.get('/api/age-groups', {
        params: {
          organizationId: organization.id
        },
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAgeGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching age groups:', error);
      setAgeGroups([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate message length (SMS has character limits)
    if (message.length > 1600) {
      setError('Message is too long. SMS messages are limited to 1600 characters.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        message,
        recipientType
      };

      // Add recipient ID(s) based on type
      if (recipientType === 'team') {
        if (!selectedTeamId) {
          setError('Please select a team');
          setLoading(false);
          return;
        }
        payload.recipientId = selectedTeamId;
      } else if (recipientType === 'user') {
        if (!selectedUserId) {
          setError('Please select a user');
          setLoading(false);
          return;
        }
        payload.recipientId = selectedUserId;
      } else if (recipientType === 'users') {
        if (!selectedUserIds || selectedUserIds.length === 0) {
          setError('Please select at least one user');
          setLoading(false);
          return;
        }
        payload.recipientIds = selectedUserIds;
      } else if (recipientType === 'ageGroup') {
        if (!selectedAgeGroupId) {
          setError('Please select an age group');
          setLoading(false);
          return;
        }
        payload.recipientId = selectedAgeGroupId;
      }

      const response = await axios.post(
        `/api/sms/organizations/${organization.id}/send-sms`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(`SMS sent successfully to ${response.data.sentCount} recipient${response.data.sentCount !== 1 ? 's' : ''}`);
        setMessage('');
        setSelectedTeamId('');
        setSelectedUserId('');
        setSelectedUserIds([]);
        setSelectedAgeGroupId('');
        
        if (onSuccess) {
          onSuccess(response.data);
        }
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to send SMS');
      }
    } catch (error) {
      console.error('Error sending SMS:', error);
      setError(error.response?.data?.message || error.message || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const characterCount = message.length;
  const characterLimit = 1600;
  const isNearLimit = characterCount > characterLimit * 0.9;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Send SMS - {organization?.name}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
              <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Recipient Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send To
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => setRecipientType('team')}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                    recipientType === 'team'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Team
                </button>
                <button
                  type="button"
                  onClick={() => setRecipientType('user')}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                    recipientType === 'user'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  User
                </button>
                <button
                  type="button"
                  onClick={() => setRecipientType('users')}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                    recipientType === 'users'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Multiple Users
                </button>
                <button
                  type="button"
                  onClick={() => setRecipientType('ageGroup')}
                  className={`px-3 py-2 text-sm font-medium rounded-lg border ${
                    recipientType === 'ageGroup'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Age Group
                </button>
              </div>
            </div>

            {/* Recipient Selection */}
            <div>
              {recipientType === 'team' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Team *
                  </label>
                  <select
                    value={selectedTeamId}
                    onChange={(e) => setSelectedTeamId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a team</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} {team.ageGroup ? `(${team.ageGroup})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {recipientType === 'user' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select User * (Only users with phone numbers are shown)
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a user</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.phone})
                      </option>
                    ))}
                  </select>
                  {users.length === 0 && (
                    <p className="mt-2 text-sm text-gray-500">No users with phone numbers available</p>
                  )}
                </div>
              )}

              {recipientType === 'users' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Users * (Only users with phone numbers are shown)
                  </label>
                  <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                    {users.length === 0 ? (
                      <p className="p-3 text-sm text-gray-500">No users with phone numbers available</p>
                    ) : (
                      users.map(user => (
                        <label
                          key={user.id}
                          className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-3 text-sm text-gray-700">
                            {user.name} ({user.phone})
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                  {selectedUserIds.length > 0 && (
                    <p className="mt-2 text-sm text-gray-600">
                      {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              )}

              {recipientType === 'ageGroup' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Age Group *
                  </label>
                  <select
                    value={selectedAgeGroupId}
                    onChange={(e) => setSelectedAgeGroupId(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select an age group</option>
                    {ageGroups.map(ageGroup => (
                      <option key={ageGroup.id} value={ageGroup.id}>
                        {ageGroup.name} {ageGroup.description ? `- ${ageGroup.description}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message * (Max 1600 characters)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={6}
                maxLength={1600}
                placeholder="Enter your SMS message here..."
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isNearLimit ? 'border-yellow-400' : ''
                }`}
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-xs text-gray-500">
                  Standard SMS: 160 characters per message. Longer messages will be split into multiple SMS.
                </p>
                <span className={`text-xs ${isNearLimit ? 'text-yellow-600 font-medium' : 'text-gray-500'}`}>
                  {characterCount} / {characterLimit}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || message.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </>
                ) : (
                  <>
                    <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                    Send SMS
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrganizationSMSModal;
